import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ArrowLeft, CheckCircle, Loader2, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { clearUrlSecret, readUrlSecret } from '../lib/urlSecrets';
import { flag } from '../config';
import lazyAccountPage from '../components/site/auth/lazyAccountPage';

// SLOT_MODEL on: the redesigned page (site redesign Phase 6), loaded on demand.
const ResetPasswordSite = lazyAccountPage(
  'ResetPasswordSite',
  () => import(/* webpackChunkName: "account-pages" */ './account/ResetPasswordSite'),
);

/** /reset-password: the redesigned page with SLOT_MODEL on, the legacy V5 page (unchanged) off. */
export default function ResetPassword() {
  return flag('SLOT_MODEL') ? <ResetPasswordSite /> : <ResetPasswordLegacy />;
}

const ResetPasswordLegacy = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // In the URL, or kept for this page by the index.html URL secret guard (lib/urlSecrets).
  const [keptToken] = useState(() => readUrlSecret('token', { location }));
  const token = searchParams.get('token') || keptToken;

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Redirect if no token
  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const validatePassword = () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validatePassword()) {
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      clearUrlSecret('token');
      setSuccess(true);
    } catch (err) {
      setError(errorMessage(err, 'Failed to reset password. The link may have expired.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return null;
  }

  // V5 Input styles
  const inputStyle = {
    height: '48px',
    padding: '0 16px',
    paddingRight: '48px',
    backgroundColor: '#FFFFFF',
    border: '2px solid rgba(30, 27, 75, 0.1)',
    borderRadius: '12px',
    fontSize: '15px',
    fontFamily: "'DM Sans', sans-serif"
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#FEF7ED' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Back to Home Link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 mb-8 transition-colors"
          style={{ color: 'rgba(30, 27, 75, 0.7)' }}
        >
          <ArrowLeft size={20} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>Back to Home</span>
        </Link>

        {/* V5 Card */}
        <div
          className="rounded-xl overflow-hidden"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(30, 27, 75, 0.05)',
            boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
            borderLeft: '4px solid #8b5cf6'
          }}
        >
          {!success ? (
            <div className="p-8">
              {/* Header */}
              <div className="text-center mb-8">
                {/* Code accent */}
                <p
                  className="mb-2"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '12px',
                    color: 'rgba(30, 27, 75, 0.3)'
                  }}
                >
                  // reset_password
                </p>

                {/* Icon */}
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                >
                  <Lock size={28} style={{ color: '#8b5cf6' }} />
                </div>

                {/* Title */}
                <h1
                  className="mb-2"
                  style={{
                    fontFamily: "'Faustina', Georgia, serif",
                    fontSize: '32px',
                    fontWeight: 500,
                    color: '#1e1b4b'
                  }}
                >
                  Reset Your Password
                </h1>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '14px',
                    color: 'rgba(30, 27, 75, 0.5)'
                  }}
                >
                  Enter your new password below.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="reset-password"
                    className="block mb-2"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'rgba(30, 27, 75, 0.7)'
                    }}
                  >
                    New Password
                  </label>
                  <div className="relative">
                    <Input
                      id="reset-password"
                      autoComplete="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="w-full focus:ring-0"
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide new password' : 'Show new password'}
                      aria-pressed={showPassword}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                    >
                      {showPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                    </button>
                  </div>
                  <p
                    className="mt-1"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '13px',
                      color: 'rgba(30, 27, 75, 0.4)'
                    }}
                  >
                    Must be at least 8 characters
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="reset-password-confirm"
                    className="block mb-2"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'rgba(30, 27, 75, 0.7)'
                    }}
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Input
                      id="reset-password-confirm"
                      autoComplete="new-password"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="w-full focus:ring-0"
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide confirmed password' : 'Show confirmed password'}
                      aria-pressed={showConfirmPassword}
                      className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                      style={{ color: 'rgba(30, 27, 75, 0.4)' }}
                    >
                      {showConfirmPassword ? <EyeOff size={20} aria-hidden="true" /> : <Eye size={20} aria-hidden="true" />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div
                    className="flex items-center gap-2 p-3 rounded-xl"
                    style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}
                  >
                    <AlertCircle size={20} style={{ color: '#ef4444' }} />
                    <p
                      className="text-sm"
                      style={{ color: '#ef4444', fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {error}
                    </p>
                  </div>
                )}

                {/* V5 Primary Button - Purple Gradient */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full font-semibold transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: '#FFFFFF',
                    height: '48px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)'
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin mr-2" size={20} />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>

              {/* Link expired? */}
              <div className="text-center mt-6">
                <p
                  className="text-sm"
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    color: 'rgba(30, 27, 75, 0.5)'
                  }}
                >
                  Link expired?{' '}
                  <Link
                    to="/forgot-password"
                    className="font-semibold hover:underline"
                    style={{ color: '#8b5cf6' }}
                  >
                    Request a new one
                  </Link>
                </p>
              </div>
            </div>
          ) : (
            /* Success State */
            <div className="p-8 text-center">
              {/* Success Icon */}
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
              >
                <CheckCircle size={28} style={{ color: '#22c55e' }} />
              </div>

              <h2
                className="mb-2"
                style={{
                  fontFamily: "'Faustina', Georgia, serif",
                  fontSize: '24px',
                  fontWeight: 500,
                  color: '#1e1b4b'
                }}
              >
                Password Reset!
              </h2>
              <p
                className="mb-6"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '14px',
                  color: 'rgba(30, 27, 75, 0.5)'
                }}
              >
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <Link to="/">
                <Button
                  className="font-semibold transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: '#FFFFFF',
                    padding: '12px 32px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'DM Sans', sans-serif",
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2)'
                  }}
                >
                  Go to Login
                </Button>
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

